import { Box, Divider, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  AvatarEmpty,
  BasicFlex,
  BasicTable,
  Button,
  Columns,
  DatePicker,
  DottedText,
  Edit,
  FilePresent,
  GeneralCardComponent,
  LabelValue,
  PageTitleAndSubTitle,
  Processing,
  RightPanelClose,
  Send,
  TextField,
  TiltedTopArrow,
  Upload,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import RiskRegisterConstants from '../../../core-utils/messages/RiskRegisterConstants';
import { NewRiskData } from '../AddRiskModalOpen';
import {
  formatActivityTable,
  getImpactIcon,
  getRiskIdChip,
  getSeverityIcon,
  getStatusChip,
  TruncatedText,
} from '../../../core-utils/helper-utils/riskRegisterHelper';
import ReportCreateNewLabelField from '../../atoms/ReportCreateNewLabelField';
import AutocompleteWithCheckBoxes from '../../atoms/Autocomplete';
import { CheckBox } from '@HomaHealth/ui-component-library';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { commonDropDownProps } from '../../atoms/ReportsCohortItem';
import { cellStyles1 } from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/BasicTable';
import axios from 'axios';
import {
  getAvatarComponent,
  getAvatarIcon,
} from '../../../core-utils/helper-utils/TaskBoardHelper';
import properties from '../../../core-utils/properties';
import { useStores } from '../../../stores';
import moment from 'moment';
import taskBoardConstants from '../../../core-utils/messages/TaskBoardConstants';
import { getTextWithToolTip } from '../../../core-utils/helper-utils/riskPatientDetailsHelper';

export interface RiskDetailsWindowProps {
  risk?: any;
  activity?: any;
  setDetailsWindowOpen: any;
   
  handleSave: (data: any) => void;
  handleOpenSnackBar: any;
  isActivityWindow?: boolean;
   
  handleActivityClick?: (data: any) => void;
  handleBack?: () => void;
  handleNavigate?: any;
}

const RiskDetailsWindow: React.FC<RiskDetailsWindowProps> = observer(
  ({
    risk,
    setDetailsWindowOpen,
    handleSave,
    handleOpenSnackBar,
    isActivityWindow,
    activity,
    handleActivityClick,
    handleBack,
    handleNavigate,
  }) => {
    //constants
    const useStyles = makeStyles()({
      container: {
        width: '38.5%',
        background: patientAccessTheme.palette.status.white,
        margin: '30px 0px 20px 20px',
        position: 'absolute',
        right: '0px',
        zIndex: 2,
        top: '29px',
      },
      root: {
        minHeight: '427px',
        height: '720px',
        width: '100%',
        padding: '20px 0px 20px 20px',
        borderLeft: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        borderTop: `1px solid ${patientAccessTheme.palette.grey[300]}`,
        overflowY: 'auto',
        overflowX: 'hidden',
        flexWrap: 'nowrap',
        gap: '15px',
      },
      tools: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
      },
      iconStyle: {
        cursor: 'pointer',
        height: '24px',
        width: '24px',
      },
      flex: {
        display: 'flex',
        gap: '10px',
      },
      desc: {
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
        padding: '4px',
        borderRadius: '4px',
        marginBottom: '20px',
      },
      scroll: {
        overflowY: 'scroll',
        ...scrollBarStyles,
      },
    });
    const { classes } = useStyles();

    //Constants
    const messages = RiskRegisterConstants;
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const ACTIVITY_TABLE_COLUMNS: Columns[] = [
      {
        name: 'id',
        cellStyles: cellStyles1,
        align: 'left',
        width: '150px',
      },
      {
        name: 'ID',
        cellStyles: cellStyles1,
        align: 'left',
        width: '150px',
      },
      {
        name: 'Name',
        cellStyles: cellStyles1,
        align: 'left',
        width: '400px',
      },
      {
        name: 'Tracker',
        cellStyles: cellStyles1,
        align: 'left',
        width: '100px',
      },
    ];

    //States
    const [data, setData] = useState<NewRiskData>({
      title: risk?.title ?? '-',
      description: risk?.description ?? '-',
      impact: risk?.impact ?? [],
      risk_status: risk?.risk_status ?? '',
      severity_level: risk?.severity_level ?? '',
      risk_source: risk?.risk_source ?? '',
      source_report: risk?.source_report ?? '',
      activity: risk?.activity[0] !== 'NA' ? risk?.activity : [],
      planned_quarter: risk?.planned_quarter,
      selected_delay_reason: risk?.selected_delay_reason,
      selected_acceptance_reason: risk?.selected_acceptance_reason,
      date_identified: risk?.date_identified,
      files: [],
      contract_id: risk?.contract_id ?? '',
      compare_agreement_id: risk?.compare_agreement_id ?? '',
    });

    const [isDescEditing, setIsDescEditing] = useState<boolean>(false);
    const [isDetailsEditing, setIsDetailsEditing] = useState<boolean>(false);
    const [comments, setComments] = useState<any>([]);
    const [newComment, setNewComment] = useState<any>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [attachments, setAttachments] = useState<any>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [activityDetails, setActivityDetails] = useState<any>(activity ?? {});
    const [trackerOptions, setTrackerOptions] = useState<any>([]);
    const [trackerValues, setTrackerValues] = useState<any>([]);
    const [trackers, setTrackers] = useState<any>([]);
    const [ownerOptions, setOwnerOptions] = useState<any>([]);

    //Stores
    const loginPageStore = useStores().loginPageStore;
    const employerGroupStore = useStores().employerGroupStore;

    //functions
    const handleCancel = (fieldName: string) => {
      switch (fieldName) {
        case 'Description':
          isActivityWindow
            ? setActivityDetails((prevData: any) => ({
                ...prevData,
                description: data?.description,
              }))
            : setData((prevData) => ({
                ...prevData,
                description: data?.description,
              }));
          setIsDescEditing(false);
        case 'Details':
          isActivityWindow
            ? setActivityDetails((prevData: any) => ({
                ...prevData,
                impact: activityDetails?.impact,
                start_date: activityDetails?.start_date,
                suggested_start_date: activityDetails?.suggested_start_date,
                primary_category: activityDetails?.primary_category,
                owner: activityDetails?.owner,
                tracker: activityDetails.tracker,
              }))
            : setData((prevData) => ({
                ...prevData,
                impact: risk?.impact,
                risk_status: risk?.risk_status,
                severity_level: risk?.severity_level,
              }));
          setIsDetailsEditing(false);
        default:
          break;
      }
    };

    const handleChange = (event: any, fieldName: string) => {
      const value = event.target.value as string;
      isActivityWindow
        ? setActivityDetails((prevData: any) => ({
            ...prevData,
            [fieldName]: value,
          }))
        : setData((prevData) => ({
            ...prevData,
            [fieldName]: value,
          }));
    };

    const handleButtonClick = () => {
      fileInputRef.current?.click();
    };

    //Apis
    const getComments = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.riskRegisterBaseUrl +
            properties.getCommentsByRiskId,
          {
            ...(isActivityWindow
              ? { activity_id: activityDetails._id }
              : { risk_id: risk._id }),
          },
        )
        .then((res: any) => {
          if (res.status === 200) {
            setComments(res.data.data);
          }
        });
    };

    const addNewComment = async () => {
      const reqBody = {
        commentText: newComment,
        ...(isActivityWindow
          ? { activity_id: activityDetails._id }
          : { risk_id: risk._id }),
        commentedBy: loginPageStore.email,
      };
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.riskRegisterBaseUrl +
            properties.addComment,
          reqBody,
        )
        .then(async (res: any) => {
          if (res.status === 200) {
            getComments();
            setIsSending(false);
            setNewComment('');
          }
        });
    };

    const getAttachments = async () => {
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.riskRegisterBaseUrl +
            properties.getAttachmentsByRiskId,
          {
            ...(isActivityWindow
              ? { activity_id: activityDetails._id }
              : { risk_id: risk._id }),
          },
        )
        .then((res: any) => {
          if (res.status === 200) {
            setAttachments(res.data.data);
          }
        });
    };

    const handleFileChange = async (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const file = event.target.files?.[0];
      if (file) {
        setIsUploading(true);
        const formData = new FormData();
        formData.append(`file`, file);
        isActivityWindow
          ? formData.append('activity_id', activityDetails._id)
          : formData.append('risk_id', risk._id);
        formData.append(
          'employer',
          employerGroupStore.employerGroupResponseData.employer_group_name[0],
        );
        await axios.post(
          properties.aiqBaseUrl +
            properties.riskRegisterBaseUrl +
            properties.uploadRiskAttachment,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        setIsUploading(false);
        getAttachments();
      }
    };

    const handleDownload = async (item: any) => {
      handleOpenSnackBar('File Download In Progress', 'info', 3000);
      await axios
        .post(
          properties.aiqBaseUrl +
            properties.contractBaseUrl +
            properties.readFile,
          {
            documentUrl: item.doc_url,
          },
        )
        .then((response) => {
          if (response.status === 200) {
            const byteCharacters = atob(response.data?.data);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteArray[i] = byteCharacters.charCodeAt(i);
            }
            const blob = new Blob([byteArray], { type: item.doc_type });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = item.doc_name;
            link.click();
            handleOpenSnackBar('File Downloaded Successfully', 'success', 5000);
          }
        })
        .catch((error) => {
          handleOpenSnackBar('File Download Failed', 'error', 6000);
          throw error;
        });
    };

    const getTrackersList = async () => {
      const roles = ['ADMIN', 'USER'];
      const requests = roles.map((role) =>
        axios.get(
          `${properties.userManagementBaseUrl}${properties.users}${properties.getByRole}/${role}`,
        ),
      );

      const responses = await Promise.all(requests);

      const allUsers = responses
        .flatMap((res) => res.data)
        .filter((user: any) =>
          user.employerGroups?.some(
            (group: any) =>
              group.egNid ===
              employerGroupStore.employerGroupResponseData.eg_nid[0],
          ),
        );
      const options = allUsers.map((item: any) =>
        getAvatarComponent(item.email, item.firstName, item.lastName),
      );
      setTrackers(allUsers);
      const values = allUsers.map((item: any) => item.email);
      setTrackerOptions(options);
      setTrackerValues(values);
    };

    const getContracts = async () => {
      const contracts = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getContracts,
        {
          eg_nid: risk?.eg_nid,
        },
      );
      const ownerOptions = contracts.data.data.map((item: any) => {
        return `${item.service_agreement_name} - ${item.health_care_partner_name}`;
      });
      setOwnerOptions(ownerOptions);
    };

    //useEffects
    useEffect(() => {
      getComments();
      getAttachments();
      if (isActivityWindow) {
        getTrackersList();
        getContracts();
      }
    }, []);

    return (
      <div className={classes.container}>
        <Grid
          container
          className={classes.root}
          sx={{
            flexDirection: 'column',
          }}
        >
          <Grid className={classes.tools}>
            {isActivityWindow && handleBack ? (
              <div style={{ marginLeft: '-5px' }} onClick={handleBack}>
                {getRiskIdChip(
                  risk?.risk_id,
                  false,
                  patientAccessTheme.palette.text.mediumemphasis,
                  { cursor: 'pointer' },
                )}
                {'  /'}
              </div>
            ) : (
              <RightPanelClose
                className={classes.iconStyle}
                onClick={() => setDetailsWindowOpen(false)}
              />
            )}
          </Grid>
          <Grid className={classes.flex}>
            {isActivityWindow
              ? getRiskIdChip(activityDetails.activity_id, true)
              : getRiskIdChip(risk.risk_id)}
          </Grid>
          <PageTitleAndSubTitle
            title={isActivityWindow ? activityDetails?.name : risk?.title}
            subTitle={''}
            subTitleStyles={{ width: '0px' }}
          />
          <Divider
            style={{ backgroundColor: patientAccessTheme.palette.grey[300] }}
          />
          <Grid className={classes.scroll} style={{ paddingRight: '20px' }}>
            <Grid>
              <Typography
                fontSize="12px"
                lineHeight="17px"
                fontType="medium"
                textColor={patientAccessTheme.palette.text.highemphasis}
                margin="0px 0px 15px 0px"
              >
                {messages.MAIN_PAGE.DESCRIPTION}
              </Typography>
              {isDescEditing ? (
                <Grid
                  className={classes.flex}
                  style={{ flexDirection: 'column', marginBottom: '20px' }}
                >
                  <TextField
                    width="495px"
                    onChange={(e: any) => handleChange(e, 'description')}
                    value={
                      isActivityWindow
                        ? activityDetails?.description
                        : data.description
                    }
                    multiline
                    inputPropsStyle={{
                      fontSize: '12px',
                      color: patientAccessTheme.palette.text.highemphasis,
                    }}
                  />
                  <Grid className={classes.flex}>
                    <Button
                      label={
                        <Typography
                          textColor={patientAccessTheme.palette.primary[500]}
                          fontSize="12px"
                          lineHeight="12px"
                          fontType={'medium'}
                        >
                          {messages.MAIN_PAGE.CANCEL}
                        </Typography>
                      }
                      variant="outlined"
                      height="36px"
                      width="60px"
                      borderRadius="4px"
                      onClick={() =>
                        handleCancel(messages.MAIN_PAGE.DESCRIPTION)
                      }
                    />
                    <Button
                      label={
                        <Typography
                          textColor={patientAccessTheme.palette.status.white}
                          fontSize="12px"
                          lineHeight="12px"
                          fontType={'medium'}
                        >
                          {messages.MAIN_PAGE.SAVE}
                        </Typography>
                      }
                      variant="contained"
                      color="primary"
                      height="36px"
                      width="60px"
                      borderRadius="4px"
                      onClick={() => {
                        handleSave({
                          ...(isActivityWindow
                            ? {
                                activity: [
                                  {
                                    _id: activityDetails._id,
                                    description: activityDetails?.description,
                                  },
                                ],
                              }
                            : {
                                risk_id: risk._id,
                                description: data.description,
                              }),
                        });
                        setIsDescEditing(false);
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Grid
                  className={classes.desc}
                  onClick={() => setIsDescEditing(true)}
                >
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      lineHeight: '17px',
                      whiteSpace: 'pre-wrap',
                      color: patientAccessTheme.palette.text.mediumemphasis,
                    }}
                  >
                    {isActivityWindow
                      ? activityDetails?.description ?? '-'
                      : data.description ?? '-'}
                  </Typography>
                </Grid>
              )}
            </Grid>
            <GeneralCardComponent
              title={messages.DETAILS_WINDOW.DETAILS}
              titleRightMostItem={
                isDetailsEditing ? (
                  <Grid className={classes.flex}>
                    <Button
                      label={
                        <Typography
                          textColor={patientAccessTheme.palette.primary[500]}
                          fontSize="12px"
                          lineHeight="12px"
                          fontType={'medium'}
                        >
                          {messages.MAIN_PAGE.CANCEL}
                        </Typography>
                      }
                      variant="outlined"
                      height="36px"
                      width="60px"
                      borderRadius="4px"
                      onClick={() =>
                        handleCancel(messages.DETAILS_WINDOW.DETAILS)
                      }
                    />
                    <Button
                      label={
                        <Typography
                          textColor={patientAccessTheme.palette.status.white}
                          fontSize="12px"
                          lineHeight="12px"
                          fontType={'medium'}
                        >
                          {messages.MAIN_PAGE.SAVE}
                        </Typography>
                      }
                      variant="contained"
                      color="primary"
                      height="36px"
                      width="60px"
                      borderRadius="4px"
                      onClick={() => {
                        handleSave(
                          isActivityWindow
                            ? {
                                activity: [
                                  {
                                    _id: activityDetails._id,
                                    impact: activityDetails?.impact,
                                    start_date: activityDetails?.start_date,
                                    suggested_start_date:
                                      activityDetails?.suggested_start_date,
                                    primary_category:
                                      activityDetails?.primary_category,
                                    owner: activityDetails?.owner,
                                    activity_status:
                                      activityDetails?.activity_status,
                                    tracker: activityDetails.tracker,
                                  },
                                ],
                              }
                            : {
                                risk_id: risk._id,
                                impact: data.impact,
                                severity_level: data.severity_level,
                                risk_status: data.risk_status,
                              },
                        );
                        setIsDetailsEditing(false);
                      }}
                    />
                  </Grid>
                ) : (
                  <Grid
                    className={classes.flex}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setIsDetailsEditing(true)}
                  >
                    <Edit stroke={patientAccessTheme.palette.primary[500]} />
                  </Grid>
                )
              }
              headerStyles={{
                justifyContent: 'center',
                backgroundColor: patientAccessTheme.palette.status.white,
                borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
              }}
              bodyStyles={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
              }}
              body={
                isDetailsEditing ? (
                  <Grid className={classes.flex} style={{ flexWrap: 'wrap' }}>
                    <ReportCreateNewLabelField
                      label={messages.DETAILS_WINDOW.IMPACT}
                      component={
                        <AutocompleteWithCheckBoxes
                          options={messages.MAIN_PAGE.IMPACT_VALUES}
                          defaultValue={
                            isActivityWindow
                              ? activityDetails?.impact
                              : data.impact
                          }
                          handleChange={(_, value) => {
                            if (isActivityWindow) {
                              setActivityDetails((prev: any) => ({
                                ...prev,
                                impact: value,
                              }));
                            } else {
                              setData((prev) => ({ ...prev, impact: value }));
                            }
                          }}
                          getOptionLabel={(option: any) => option}
                          placeholder={messages.DETAILS_WINDOW.IMPACT}
                          width="279px"
                          renderOption={(option: any, { selected }: any) => {
                            const index = messages.MAIN_PAGE.IMPACT_VALUES.indexOf(
                              option,
                            );
                            return (
                              <Box
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <CheckBox
                                  checked={selected}
                                  id=""
                                  name=""
                                  color="primary"
                                />
                                {messages.MAIN_PAGE.IMPACT_OPTIONS[index]}
                              </Box>
                            );
                          }}
                          noOptionsText="No Impact Type Found"
                        />
                      }
                      marginBottom="12px"
                    />
                    {isActivityWindow && (
                      <>
                        <ReportCreateNewLabelField
                          label={messages.MAIN_PAGE.START_DATE}
                          component={
                            <DatePicker
                              width="279px"
                              handleChange={(value) => {
                                setActivityDetails((prev: any) => ({
                                  ...prev,
                                  start_date: value,
                                }));
                              }}
                              selectedDate={
                                activityDetails.start_date
                                  ? moment
                                      .utc(activityDetails.start_date)
                                      .format('MMM D, YYYY')
                                  : null
                              }
                              minDate={'Jan 01, 2020'}
                              maxDate={moment().add(1, 'years')}
                              format={'MMMM d, yyyy'}
                              placeholder={messages.MAIN_PAGE.DATE_FORMAT}
                              error={
                                activityDetails?.activity_status !==
                                  'Suggested' && !activityDetails.start_date
                              }
                            />
                          }
                        />
                        {activityDetails?.activity_status === 'Suggested' && (
                          <ReportCreateNewLabelField
                            label={taskBoardConstants.SUGGESTED_START_DATE}
                            component={
                              <DatePicker
                                width="279px"
                                handleChange={(value) => {
                                  setActivityDetails((prev: any) => ({
                                    ...prev,
                                    suggested_start_date: value,
                                  }));
                                }}
                                selectedDate={
                                  activityDetails.suggested_start_date
                                    ? moment
                                        .utc(
                                          activityDetails.suggested_start_date,
                                        )
                                        .format('MMMM D, YYYY')
                                    : null
                                }
                                minDate={'Jan 01, 2020'}
                                maxDate={moment().add(1, 'years')}
                                format={'MMMM d, yyyy'}
                                placeholder={messages.MAIN_PAGE.DATE_FORMAT}
                                error={!activityDetails.suggested_start_date}
                              />
                            }
                          />
                        )}
                        <ReportCreateNewLabelField
                          label={taskBoardConstants.ACT_CATEGORY}
                          component={
                            <Dropdown
                              label={taskBoardConstants.ACT_CATEGORY}
                              dropDownOptions={[
                                getTextWithToolTip(
                                  activityDetails.primary_category,
                                  25,
                                ),
                                ...taskBoardConstants.CATEGORY_OPTIONS.map(
                                  (item: any) => getTextWithToolTip(item, 25),
                                ),
                              ]}
                              dropDownValues={[
                                activityDetails.primary_category,
                                ...taskBoardConstants.CATEGORY_OPTIONS,
                              ]}
                              value={activityDetails?.primary_category}
                              setValue={(value: string) => {
                                setActivityDetails((prev: any) => ({
                                  ...prev,
                                  primary_category: value,
                                }));
                              }}
                              selectStyles={{
                                ...commonDropDownProps.selectStyles,
                                width: '279px',
                              }}
                              labelTop="10px"
                              onChange={(event: any) => {
                                setActivityDetails((prev: any) => ({
                                  ...prev,
                                  primary_category: event.target.value,
                                }));
                              }}
                            />
                          }
                        />
                        <ReportCreateNewLabelField
                          label={messages.MAIN_PAGE.OWNER}
                          component={
                            <AutocompleteWithCheckBoxes
                              options={ownerOptions}
                              defaultValue={activityDetails.owner}
                              handleChange={(_, value) => {
                                setActivityDetails((prev: any) => ({
                                  ...prev,
                                  owner: value,
                                }));
                              }}
                              typable
                              getOptionLabel={(option: any) => option}
                              placeholder={messages.MAIN_PAGE.OWNER}
                              width="279px"
                              renderOption={(
                                option: any,
                                { selected }: any,
                              ) => {
                                const index = ownerOptions.indexOf(option);
                                return (
                                  <Box
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <CheckBox
                                      checked={selected}
                                      id=""
                                      name=""
                                      color="primary"
                                    />
                                    {getTextWithToolTip(
                                      ownerOptions[index],
                                      20,
                                    )}
                                  </Box>
                                );
                              }}
                              noOptionsText="No Owner Found"
                            />
                          }
                          marginBottom="12px"
                        />
                        <ReportCreateNewLabelField
                          label={messages.MAIN_PAGE.TRACKER}
                          component={
                            <Dropdown
                              label={messages.MAIN_PAGE.TRACKER}
                              dropDownOptions={trackerOptions}
                              dropDownValues={trackerValues}
                              value={activityDetails.tracker}
                              setValue={(value: string) => {
                                setActivityDetails((prev: any) => ({
                                  ...prev,
                                  tracker: value,
                                }));
                              }}
                              selectStyles={{
                                ...commonDropDownProps.selectStyles,
                                width: '279px',
                              }}
                              labelTop="10px"
                              onChange={(event: any) => {
                                setActivityDetails((prev: any) => ({
                                  ...prev,
                                  tracker: event.target.value,
                                }));
                              }}
                            />
                          }
                        />
                        <ReportCreateNewLabelField
                          label={messages.MAIN_PAGE.RISK_STATUS}
                          component={
                            <Dropdown
                              label={messages.MAIN_PAGE.RISK_STATUS}
                              dropDownOptions={
                                taskBoardConstants.STATUS_OPTIONS
                              }
                              dropDownValues={taskBoardConstants.STATUS_VALUES}
                              value={activityDetails.activity_status}
                              setValue={(value: string) => {
                                setActivityDetails((prev: any) => ({
                                  ...prev,
                                  activity_status: value,
                                }));
                              }}
                              selectStyles={{
                                ...commonDropDownProps.selectStyles,
                                width: '279px',
                              }}
                              labelTop="10px"
                              onChange={(event: any) => {
                                setActivityDetails((prev: any) => ({
                                  ...prev,
                                  activity_status: event.target.value,
                                }));
                              }}
                            />
                          }
                        />
                      </>
                    )}
                    {!isActivityWindow && (
                      <>
                        <ReportCreateNewLabelField
                          label={messages.DETAILS_WINDOW.STATUS}
                          component={
                            <Dropdown
                              label={messages.DETAILS_WINDOW.STATUS}
                              dropDownOptions={
                                messages.MAIN_PAGE.STATUS_OPTIONS
                              }
                              dropDownValues={messages.MAIN_PAGE.STATUS_VALUES}
                              value={data.risk_status}
                              setValue={(value: string) =>
                                setData((prevData) => ({
                                  ...prevData,
                                  risk_status: value,
                                }))
                              }
                              selectStyles={{
                                ...commonDropDownProps.selectStyles,
                                width: '279px',
                              }}
                              labelTop="10px"
                              onChange={(event: any) =>
                                handleChange(event, 'risk_status')
                              }
                              error={!data.risk_status}
                            />
                          }
                        />
                        <ReportCreateNewLabelField
                          label={messages.DETAILS_WINDOW.SEVERITY}
                          component={
                            <Dropdown
                              label={messages.DETAILS_WINDOW.SEVERITY}
                              dropDownOptions={
                                messages.MAIN_PAGE.SEVERITY_LEVEL_OPTIONS
                              }
                              dropDownValues={
                                messages.MAIN_PAGE.SEVERITY_LEVEL_VALUES
                              }
                              value={data.severity_level}
                              setValue={(value: string) =>
                                setData((prevData) => ({
                                  ...prevData,
                                  severity_level: value,
                                }))
                              }
                              selectStyles={{
                                ...commonDropDownProps.selectStyles,
                                width: '279px',
                              }}
                              labelTop="10px"
                              onChange={(event: any) =>
                                handleChange(event, 'severity_level')
                              }
                              error={!data.severity_level}
                            />
                          }
                        />
                        <LabelValue
                          label={messages.DETAILS_WINDOW.SOURCE}
                          value={
                            <div
                              className={classes.flex}
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() => {}}
                            >
                              <DottedText
                                typographyStyles={{
                                  fontSize: '14px',
                                  lineHeight: '24px',
                                  fontWeight: 500,
                                  color:
                                    patientAccessTheme.palette.primary[500],
                                }}
                                text={data?.source_report ?? data?.risk_source}
                              />
                              <TiltedTopArrow
                                style={{
                                  width: '10px',
                                  height: '11px',
                                  stroke:
                                    patientAccessTheme.palette.primary[500],
                                }}
                              />
                            </div>
                          }
                          labelsMinWidth="100px"
                        />
                      </>
                    )}
                  </Grid>
                ) : (
                  <>
                    {isActivityWindow && (
                      <>
                        <LabelValue
                          label={messages.DETAILS_WINDOW.CONNECTED}
                          value={
                            <Grid container spacing={1}>
                              {activityDetails?.connected_to?.map(
                                (imp: any, idx: number) => (
                                  <Grid key={idx}>
                                    {getRiskIdChip(
                                      imp?.risk_id,
                                      false,
                                      patientAccessTheme.palette.text
                                        .mediumemphasis,
                                    )}
                                  </Grid>
                                ),
                              )}
                            </Grid>
                          }
                          labelsMinWidth="100px"
                        />
                        <LabelValue
                          label={taskBoardConstants.ACT_CATEGORY}
                          value={
                            <TruncatedText
                              label={activityDetails?.primary_category}
                              width="400px"
                            />
                          }
                          labelsMinWidth="100px"
                        />
                      </>
                    )}
                    <LabelValue
                      label={messages.DETAILS_WINDOW.IMPACT}
                      value={
                        <Grid container spacing={1}>
                          {(isActivityWindow
                            ? activityDetails?.impact
                            : data?.impact
                          )?.map((imp: string, idx: number) => (
                            <Grid key={idx}>{getImpactIcon(imp)}</Grid>
                          ))}
                        </Grid>
                      }
                      labelsMinWidth="100px"
                    />
                    {risk && (
                      <>
                        <LabelValue
                          label={messages.DETAILS_WINDOW.STATUS}
                          value={getStatusChip(data.risk_status)}
                          labelsMinWidth="100px"
                        />
                        <LabelValue
                          label={messages.DETAILS_WINDOW.SEVERITY}
                          value={getSeverityIcon(data.severity_level)}
                          labelsMinWidth="100px"
                        />
                      </>
                    )}
                    {isActivityWindow && (
                      <LabelValue
                        label={messages.DETAILS_WINDOW.STATUS}
                        value={
                          <Grid container spacing={1}>
                            {getStatusChip(activityDetails?.activity_status)}
                          </Grid>
                        }
                        labelsMinWidth="100px"
                      />
                    )}
                    {isActivityWindow && (
                      <>
                        <LabelValue
                          label={messages.MAIN_PAGE.START_DATE}
                          value={
                            activityDetails?.start_date
                              ? moment
                                  .utc(activity?.start_date)
                                  .format('MMMM D, YYYY')
                              : '-'
                          }
                          labelsMinWidth="100px"
                        />
                        {activityDetails?.activity_status === 'Suggested' && (
                          <LabelValue
                            label={taskBoardConstants.SUGGESTED_START_DATE}
                            value={
                              activityDetails?.suggested_start_date
                                ? moment
                                    .utc(activity?.suggested_start_date)
                                    .format('MMMM D, YYYY')
                                : '-'
                            }
                            labelsMinWidth="100px"
                          />
                        )}
                        <LabelValue
                          label={messages.MAIN_PAGE.OWNER}
                          value={
                            <TruncatedText
                              label={activityDetails?.owner.join(',')}
                              width="400px"
                            />
                          }
                          labelsMinWidth="100px"
                        />
                        <LabelValue
                          label={messages.MAIN_PAGE.TRACKER}
                          value={(() => {
                            const trackerEmail = activityDetails?.tracker;
                            const matchedTracker = trackers?.find(
                              (t: any) => t.email === trackerEmail,
                            );
                            return trackerEmail ? (
                              getAvatarComponent(
                                trackerEmail,
                                matchedTracker?.firstName,
                                matchedTracker?.lastName,
                              )
                            ) : (
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '5px',
                                  alignItems: 'center',
                                }}
                              >
                                <AvatarEmpty />
                                None
                              </div>
                            );
                          })()}
                          labelsMinWidth="100px"
                        />
                      </>
                    )}
                    {!isActivityWindow && (
                      <LabelValue
                        label={messages.DETAILS_WINDOW.SOURCE}
                        value={
                          <div
                            className={classes.flex}
                            style={{
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              handleNavigate(
                                risk?.contract_id,
                                risk?.source_report,
                                risk?.compare_agreement_id,
                                handleOpenSnackBar,
                              )
                            }
                          >
                            <DottedText
                              typographyStyles={{
                                fontSize: '14px',
                                lineHeight: '24px',
                                fontWeight: 500,
                                color: patientAccessTheme.palette.primary[500],
                              }}
                              text={data?.source_report ?? data?.risk_source}
                            />
                            <TiltedTopArrow
                              style={{
                                width: '10px',
                                height: '11px',
                                stroke: patientAccessTheme.palette.primary[500],
                              }}
                            />
                          </div>
                        }
                        labelsMinWidth="100px"
                      />
                    )}
                  </>
                )
              }
              titleFontSize="12px"
              titleFontType="medium"
              mainGridStyles={{ marginBottom: '20px', marginTop: '20px' }}
            />
            {!isActivityWindow && (
              <>
                <Typography
                  style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    lineHeight: '17px',
                    color: patientAccessTheme.palette.text.highemphasis,
                  }}
                >
                  {messages.DETAILS_WINDOW.ACTIVITY}
                </Typography>
                {data?.activity?.length > 0 ? (
                  <BasicTable
                    columns={ACTIVITY_TABLE_COLUMNS}
                    rowsData={formatActivityTable(
                      data.activity,
                      handleActivityClick,
                    )}
                    hideColumnIndex={0}
                    hideTableHeader={true}
                    rootStyles={{ marginTop: '15px', marginBottom: '15px' }}
                    displayType={'row'}
                    width="610px"
                  />
                ) : (
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: '500',
                      lineHeight: '17px',
                      color: patientAccessTheme.palette.text.mediumemphasis,
                      margin: '15px',
                    }}
                  >
                    {messages.DETAILS_WINDOW.NO_ACTIVITIES}
                  </Typography>
                )}
              </>
            )}
            <GeneralCardComponent
              title={messages.DETAILS_WINDOW.COMMENTS}
              titleFontSize="12px"
              titleFontType="medium"
              headerStyles={{
                backgroundColor: patientAccessTheme.palette.status.white,
                borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
              }}
              bodyStyles={{
                maxHeight: '250px',
                paddingRight: '0px',
              }}
              body={
                <Grid
                  container
                  className={classes.scroll}
                  style={{
                    gap: '15px',
                    maxHeight: '210px',
                    flexWrap: 'nowrap',
                  }}
                  sx={{
                    flexDirection: 'column',
                  }}
                >
                  {comments?.length > 0 &&
                    comments.map((item: any) => (
                      <Grid
                        key={item._id}
                        style={{
                          display: 'flex',
                          justifyContent:
                            item?.commentedBy === loginPageStore.email
                              ? 'right'
                              : 'left',
                          paddingRight: '20px',
                          gap: '5px',
                        }}
                      >
                        {item?.commentedBy === loginPageStore.email ? (
                          <>
                            <BasicFlex
                              layoutStyles={{
                                width: 'fit-content',
                                height: 'fit-content',
                                padding: '7px',
                                borderTopLeftRadius: '5px',
                                margin: '5px',
                              }}
                              body={
                                <Typography
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    lineHeight: '17px',
                                    whiteSpace: 'pre-wrap',
                                    color:
                                      patientAccessTheme.palette.text
                                        .mediumemphasis,
                                  }}
                                >
                                  {item.commentText}
                                </Typography>
                              }
                            />
                            {getAvatarIcon(item?.commentedBy)}
                          </>
                        ) : (
                          <>
                            {getAvatarIcon(item?.commentedBy)}
                            <BasicFlex
                              layoutStyles={{
                                width: 'fit-content',
                                height: 'fit-content',
                                padding: '7px',
                                borderTopRightRadius: '5px',
                                margin: '5px',
                              }}
                              body={
                                <Typography
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    lineHeight: '17px',
                                    whiteSpace: 'pre-wrap',
                                    color:
                                      patientAccessTheme.palette.text
                                        .mediumemphasis,
                                  }}
                                >
                                  {item.commentText}
                                </Typography>
                              }
                            />
                          </>
                        )}
                      </Grid>
                    ))}
                </Grid>
              }
              footer={
                <>
                  {getAvatarIcon(
                    loginPageStore.email,
                    loginPageStore.firstName,
                  )}
                  <TextField
                    placeholder={messages.DETAILS_WINDOW.ADD_COMMENT}
                    width="420px"
                    onChange={(e: any) => setNewComment(e.target.value)}
                    value={newComment}
                    inputPropsStyle={{ minHeight: '45px' }}
                    endAdornment={
                      isSending ? (
                        <Typography
                          fontSize="9px"
                          lineHeight="12px"
                          fontType="medium"
                          textColor={
                            patientAccessTheme.palette.text.mediumemphasis
                          }
                        >
                          {messages.DETAILS_WINDOW.SENDING}
                        </Typography>
                      ) : (
                        <Send
                          onClick={() => {
                            addNewComment();
                            setIsSending(true);
                          }}
                          style={{ cursor: 'pointer' }}
                        />
                      )
                    }
                    onKeyDown={(e: any) => {
                      if (e.key === 'Enter') {
                        addNewComment(); // Trigger the send function when Enter is pressed
                        setIsSending(true);
                      }
                    }}
                  />
                </>
              }
            />
            <GeneralCardComponent
              title={messages.MAIN_PAGE.ATTACHMENTS}
              titleFontSize="12px"
              titleFontType="medium"
              titleColor={patientAccessTheme.palette.text.mediumemphasis}
              titleRightMostItem={
                <>
                  <Button
                    label={
                      <Typography
                        textColor={patientAccessTheme.palette.primary[500]}
                        fontSize="12px"
                        lineHeight="12px"
                        fontType={'medium'}
                      >
                        {isUploading
                          ? messages.DETAILS_WINDOW.UPLOADING
                          : messages.DETAILS_WINDOW.UPLOAD}
                      </Typography>
                    }
                    startIcon={
                      isUploading ? (
                        <Processing
                          stroke={patientAccessTheme.palette.primary[500]}
                        />
                      ) : (
                        <Upload
                          stroke={patientAccessTheme.palette.primary[500]}
                        />
                      )
                    }
                    variant={'outlined'}
                    color="primary"
                    height="36px"
                    width={'fit-content'}
                    borderRadius="4px"
                    onClick={handleButtonClick}
                    disabled={isUploading}
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </>
              }
              headerStyles={{
                backgroundColor: patientAccessTheme.palette.status.white,
                height: '76px',
                justifyContent: 'center',
              }}
              bodyStyles={{
                display: 'flex',
                flexDirection: 'column',
                gap: '15px',
                paddingTop: '0px',
              }}
              body={
                <>
                  {attachments?.length > 0 &&
                    attachments.map((item: any) => (
                      <UploadedDocument
                        name={item.doc_name}
                        size={item.doc_size}
                        startIcon={
                          <FilePresent
                            stroke={patientAccessTheme.palette.primary[500]}
                            fill={patientAccessTheme.palette.primary[500]}
                          />
                        }
                        hideDelete={true}
                        handleOnClick={() => handleDownload(item)}
                      />
                    ))}
                </>
              }
              mainGridStyles={{ border: '0px' }}
            />
          </Grid>
        </Grid>
      </div>
    );
  },
);
export default RiskDetailsWindow;
